import { Component, Inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { School } from '../../../../models/school.model';
import { SchoolResource } from '../../../../models/school-resource.model';
import { RtI18nDirective } from '../../runtime-translation.directive';
import { SchoolResourcesService } from '../../school-resources.service';
import { GradeLevelOption } from '../../grade-levels';
import { ServiceStatus } from '../../service-status.enum';

export interface SchoolResourcesDialogData {
  school: School;
  /**
   * Distinct grade levels of the guardian's students at this school. Resources are authored
   * per school + grade, so all of them are fetched and merged, and items that only apply to
   * some of these grades get labeled. Empty → the school's ungraded superset is shown
   * (e.g. staff, or no student records).
   */
  gradeOptions: GradeLevelOption[];
}

/** A merged CMS item plus the family grades it's specific to (empty = applies to all of them). */
interface MergedResource {
  item: SchoolResource;
  gradeNames: string[];
}

/**
 * "{School} Resources" dialog opened from a main-menu school card's Resources pill. Two
 * sources: static links resolved from the public nhaschools.com CMS (moving / before & after
 * care), plus the school's authored resource list (links + PDF documents) proxied through our
 * server. The lists for every family grade at the school are fetched in parallel and merged —
 * shared items show once, grade-specific ones are labeled with their grades. Opened via
 * AdaptiveContainerService: centered dialog on desktop, bottom sheet on mobile.
 */
@Component({
  selector: 'app-school-resources-modal',
  standalone: true,
  templateUrl: './school-resources-modal.component.html',
  styleUrl: './school-resources-modal.component.scss',
  imports: [MatDialogModule, MatProgressSpinner, DatePipe, RtI18nDirective]
})
export class SchoolResourcesModalComponent implements OnInit {
  readonly ServiceStatus = ServiceStatus;

  readonly school: School;
  readonly gradeOptions: GradeLevelOption[];

  // Shown to every family unconditionally, matching the Scholar portal.
  readonly movingUrl: string;

  // Set only when the CMS says the school offers before & after school care.
  beforeAfterCareUrl: string | null = null;

  // Merged CMS items across the family's grades, split by presentation (API order preserved).
  resourceLinks: MergedResource[] = [];
  resourceDocuments: MergedResource[] = [];
  resourcesStatus = ServiceStatus.unset;

  constructor(
    public dialogRef: MatDialogRef<SchoolResourcesModalComponent>,
    @Inject(MAT_DIALOG_DATA) data: SchoolResourcesDialogData,
    private schoolResourcesService: SchoolResourcesService
  ) {
    this.school = data.school;
    this.gradeOptions = data.gradeOptions ?? [];
    this.movingUrl = schoolResourcesService.movingUrl;
  }

  ngOnInit(): void {
    if (this.school.SchoolEID) {
      this.schoolResourcesService.getBeforeAfterCareUrl(this.school.SchoolEID)
        .subscribe(url => this.beforeAfterCareUrl = url);
      this.loadResources();
    }
  }

  /** Unknown types surface their raw CMS type text as the subtitle, like the Scholar portal. */
  linkSubtitle(entry: MergedResource): string | null {
    const type = entry.item.Type?.toLowerCase();
    return type && type !== 'link' ? entry.item.Type : null;
  }

  private isPdf(item: SchoolResource): boolean {
    return item.Type?.toLowerCase() === 'pdf';
  }

  /**
   * Loads the authored resources for every family grade at the school in parallel (the server
   * caches per school + grade pair) and merges them. Fail-soft: an error just leaves the CMS
   * sections empty — the static links above still render.
   */
  private loadResources(): void {
    this.resourcesStatus = ServiceStatus.loading;

    const fetches = this.gradeOptions.length > 0
      ? this.gradeOptions.map(g => this.schoolResourcesService.getResources(this.school.SchoolEID, g.id))
      : [this.schoolResourcesService.getResources(this.school.SchoolEID)];

    forkJoin(fetches).subscribe({
      next: (lists: SchoolResource[][]) => {
        const merged = this.mergeResources(lists);
        this.resourceDocuments = merged.filter(m => this.isPdf(m.item));
        this.resourceLinks = merged.filter(m => !this.isPdf(m.item));
        this.resourcesStatus = ServiceStatus.loaded;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading school resources:', error);
        this.resourceLinks = [];
        this.resourceDocuments = [];
        this.resourcesStatus = ServiceStatus.error;
      }
    });
  }

  /**
   * Union of the per-grade lists (API order preserved, first appearance wins), each item
   * tracking which of the family's grades returned it. Items present for every queried grade
   * aren't grade-specific, so their labels are cleared and they render unmarked.
   */
  private mergeResources(lists: SchoolResource[][]): MergedResource[] {
    const merged = new Map<string, MergedResource>();

    lists.forEach((list, index) => {
      const gradeName = this.gradeOptions[index]?.name;
      for (const item of list) {
        const key = `${item.Type}|${item.Resource}|${item.Title}`;
        let entry = merged.get(key);
        if (!entry) {
          entry = { item, gradeNames: [] };
          merged.set(key, entry);
        }
        if (gradeName && !entry.gradeNames.includes(gradeName)) {
          entry.gradeNames.push(gradeName);
        }
      }
    });

    const result = [...merged.values()];
    for (const entry of result) {
      if (this.gradeOptions.length <= 1 || entry.gradeNames.length >= this.gradeOptions.length) {
        entry.gradeNames = [];
      }
    }
    return result;
  }
}
